/**
 * OnboardingFlow — fullscreen onboarding overlay.
 *
 * Shown when:
 *   - user is authenticated
 *   - user.onboardingCompleted === false
 *
 * 5 steps (index 0..4):
 *   0 — Welcome (MCP-first intro)
 *   1 — Generate API Token
 *   2 — MCP Setup Instructions (tabs per client)
 *   3 — Terms of Service
 *   4 — Done
 *
 * Persistence: after each step transition, PATCH /api/auth/onboarding is called
 * (optimistic — errors are logged to console only).
 *
 * Skip: allowed only when terms are accepted. If not, show inline message.
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuthStore } from '../../stores/authStore';
import { usePaperTradingStore } from '../../stores/paperTradingStore';
import { listApiTokens, updateOnboarding } from '../../api/client';
import type { ApiTokenCreated } from '../../types';

import { StepWelcome } from './StepWelcome';
import { StepToken } from './StepToken';
import { StepMcpSetup } from './StepMcpSetup';
import { StepDone } from './StepDone';
import { BrandWordmark } from '../BrandLogo';

const TOTAL_STEPS = 4;

// ---------------------------------------------------------------------------
// Progress indicator
// ---------------------------------------------------------------------------

interface ProgressProps {
  current: number; // 0-indexed
  total: number;
}

function ProgressIndicator({ current, total }: ProgressProps) {
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
            i < current
              ? 'bg-primary-700/60'
              : i === current
                ? 'bg-primary-400'
                : 'bg-gray-700'
          }`}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// OnboardingShell — wraps step content in the card + layout
// ---------------------------------------------------------------------------

interface OnboardingShellProps {
  currentStep: number;
  onSkip: () => void;
  showSkipError: boolean;
  children: React.ReactNode;
}

function OnboardingShell({ currentStep, onSkip, showSkipError, children }: OnboardingShellProps) {
  return (
    <div
      className="z-50 app-bg flex flex-col overflow-auto"
      style={{ position: 'fixed', inset: 0 }}
      role="dialog"
      aria-modal="true"
      aria-label="Onboarding"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4 shrink-0">
        <BrandWordmark className="text-lg" />
        <div className="flex items-center gap-3">
          {showSkipError && (
            <p className="text-xs text-amber-400">
              You must accept the Terms of Service before skipping.
            </p>
          )}
          <button
            type="button"
            onClick={onSkip}
            className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
          >
            Skip onboarding
          </button>
        </div>
      </div>

      {/* Center card */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="glass-panel rounded-2xl p-8 sm:p-10 w-full max-w-2xl">
          {/* Progress */}
          <div className="mb-6 space-y-2">
            <ProgressIndicator current={currentStep} total={TOTAL_STEPS} />
            <p className="text-xs text-gray-500 text-right">
              Step {currentStep + 1} of {TOTAL_STEPS}
            </p>
          </div>

          {/* Step content */}
          {children}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function OnboardingFlow() {
  const user = useAuthStore((s) => s.user);
  const updateUser = useAuthStore((s) => s.updateUser);
  const setActivePage = usePaperTradingStore((s) => s.setActivePage);

  // Determine initial step from persisted server state
  const initialStep = user?.onboardingCurrentStep ?? 0;

  const [currentStep, setCurrentStep] = useState(initialStep);
  const [generatedToken, setGeneratedToken] = useState<ApiTokenCreated | null>(null);
  const [hasExistingTokens, setHasExistingTokens] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSkipError, setShowSkipError] = useState(false);

  // Skip a fresh generation if the user already created one in a previous
  // session — we don't want a graveyard of identical onboarding tokens in
  // the Settings panel.
  useEffect(() => {
    let cancelled = false;
    listApiTokens()
      .then((tokens) => {
        if (!cancelled) setHasExistingTokens(tokens.length > 0);
      })
      .catch((err) => {
        console.error('[OnboardingFlow] Failed to list existing tokens:', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // If user is not loaded or onboarding is already completed — render nothing
  if (!user || user.onboardingCompleted) {
    return null;
  }

  // Narrowed non-null reference — safe to use in handlers below
  const currentUser = user;

  // ---------------------------------------------------------------------------
  // Persistence helper
  // ---------------------------------------------------------------------------

  async function persistStep(step: number, extra: { acceptedTerms?: boolean; completed?: boolean } = {}) {
    try {
      const updated = await updateOnboarding({ currentStep: step, ...extra });
      updateUser({
        onboardingCurrentStep: updated.onboardingCurrentStep,
        onboardingCompleted: updated.onboardingCompleted,
        termsAcceptedAt: updated.termsAcceptedAt,
        onboardingCompletedAt: updated.onboardingCompletedAt,
      });
    } catch (err) {
      console.error('[OnboardingFlow] Failed to persist step:', err);
    }
  }

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------

  function handleNext() {
    const next = currentStep + 1;
    setCurrentStep(next);
    void persistStep(next);
  }

  function handleBack() {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }

  // ---------------------------------------------------------------------------
  // Skip
  // ---------------------------------------------------------------------------

  function handleSkip() {
    const termsAlreadyAccepted = !!currentUser.termsAcceptedAt || termsAccepted;
    if (!termsAlreadyAccepted) {
      setShowSkipError(true);
      setTimeout(() => setShowSkipError(false), 4000);
      return;
    }
    setIsSaving(true);
    updateOnboarding({ completed: true })
      .then((updated) => {
        updateUser({
          onboardingCompleted: updated.onboardingCompleted,
          onboardingCompletedAt: updated.onboardingCompletedAt,
        });
      })
      .catch((err) => console.error('[OnboardingFlow] Skip failed:', err))
      .finally(() => setIsSaving(false));
  }

  // ---------------------------------------------------------------------------
  // Step 1: token gate
  // ---------------------------------------------------------------------------

  function handleTokenGenerated(token: ApiTokenCreated) {
    setGeneratedToken(token);
    setHasExistingTokens(true);
  }

  const step1NextEnabled = generatedToken !== null || hasExistingTokens;

  // ---------------------------------------------------------------------------
  // Step 3: done — accepts terms and completes onboarding in one shot
  // ---------------------------------------------------------------------------

  async function handleGoToDashboard() {
    setIsSaving(true);
    try {
      const updated = await updateOnboarding({
        acceptedTerms: true,
        completed: true,
        currentStep: 3,
      });
      updateUser({
        onboardingCompleted: updated.onboardingCompleted,
        onboardingCompletedAt: updated.onboardingCompletedAt,
        termsAcceptedAt: updated.termsAcceptedAt,
      });
      setActivePage('backtesting');
    } catch (err) {
      console.error('[OnboardingFlow] Complete failed:', err);
    } finally {
      setIsSaving(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const mcpToken = generatedToken?.token ?? null;

  return createPortal(
    <OnboardingShell
      currentStep={currentStep}
      onSkip={handleSkip}
      showSkipError={showSkipError}
    >
      {currentStep === 0 && (
        <StepWelcome onNext={handleNext} />
      )}

      {currentStep === 1 && (
        <StepToken
          token={generatedToken}
          hasExistingTokens={hasExistingTokens}
          onTokenGenerated={handleTokenGenerated}
          onNext={handleNext}
          onBack={handleBack}
          nextEnabled={step1NextEnabled}
        />
      )}

      {currentStep === 2 && (
        <StepMcpSetup
          token={mcpToken}
          onNext={handleNext}
          onBack={handleBack}
        />
      )}

      {currentStep === 3 && (
        <StepDone
          accepted={termsAccepted}
          onAcceptedChange={setTermsAccepted}
          onGoToDashboard={handleGoToDashboard}
          onBack={handleBack}
          isLoading={isSaving}
        />
      )}
    </OnboardingShell>,
    document.body,
  );
}
